(() => {
  if (customElements.get('skin-orientation')) return;

  const tendencyCopy = {
    dry: 'Reportaste una sensación de sequedad o tirantez que vale la pena conversar durante una valoración.',
    mixed: 'Reportaste diferencias entre zonas del rostro, con áreas que pueden sentirse más secas y otras con mayor brillo.',
    oily: 'Reportaste brillo frecuente o una sensación grasa en varias zonas del rostro.',
    balanced: 'Reportaste una sensación generalmente cómoda, sin una tendencia dominante en esta orientación.'
  };

  const priorityCopy = {
    breakouts: 'Brotes e imperfecciones',
    spots: 'Manchas y uniformidad del tono',
    lines: 'Líneas visibles',
    firmness: 'Cambios en firmeza',
    sensitivity: 'Sensibilidad y confort',
    'dark-circles': 'Apariencia del contorno de ojos',
    hydration: 'Hidratación y confort'
  };

  const symptomCopy = {
    breakouts: 'Brotes o imperfecciones',
    blackheads: 'Puntos negros',
    'visible-pores': 'Poros visibles',
    spots: 'Manchas o tono disparejo',
    redness: 'Enrojecimiento',
    'dark-circles': 'Ojeras',
    lines: 'Líneas visibles',
    firmness: 'Cambios en firmeza',
    marks: 'Marcas persistentes',
    sensitivity: 'Sensibilidad',
    dehydration: 'Falta de hidratación'
  };

  class SkinOrientation extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this.answers = {};
      this.lastDialogOpener = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.experience = this.querySelector('[data-orientation-experience]');
      this.startButton = this.querySelector('[data-start-orientation]');
      this.intro = this.querySelector('[data-orientation-intro]');
      this.steps = Array.from(this.querySelectorAll('[data-orientation-step]'));
      this.progressbar = this.querySelector('[data-progressbar]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.dialog = this.querySelector('[data-diagnostic-handoff]');

      if (!this.experience || !this.startButton || this.steps.length !== 5) return;

      this.startButton.hidden = false;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      if (this.dialog) {
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('keydown', this.onDialogKeydown);
        this.dialog.addEventListener('close', this.onDialogClose);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);

      if (this.dialog) {
        this.dialog.removeEventListener('click', this.onDialogClick);
        this.dialog.removeEventListener('keydown', this.onDialogKeydown);
        this.dialog.removeEventListener('close', this.onDialogClose);
      }
    }

    onClick(event) {
      const start = event.target.closest('[data-start-orientation]');
      const next = event.target.closest('[data-next-step]');
      const previous = event.target.closest('[data-previous-step]');
      const results = event.target.closest('[data-show-results]');
      const reset = event.target.closest('[data-reset-orientation]');
      const handoffOpen = event.target.closest('[data-handoff-open]');
      const handoffClose = event.target.closest('[data-handoff-close]');

      if (start) {
        this.start();
      } else if (next && this.validateStep(this.currentStep)) {
        this.showStep(this.currentStep + 1);
      } else if (previous) {
        this.showStep(this.currentStep - 1);
      } else if (results && this.validateStep(this.currentStep)) {
        this.captureAnswers();
        this.renderResults();
        this.showStep(4);
      } else if (reset) {
        this.resetOrientation();
      } else if (handoffOpen) {
        this.openHandoff(handoffOpen);
      } else if (handoffClose && this.dialog?.open) {
        this.dialog.close();
      }
    }

    onChange(event) {
      const input = event.target.closest('input');
      if (!input) return;

      const group = input.closest('[data-required-group]');
      if (input.name === 'symptoms') {
        const symptomInputs = Array.from(this.querySelectorAll('input[name="symptoms"]'));
        const noneOption = symptomInputs.find((item) => item.hasAttribute('data-none-option'));

        if (input.hasAttribute('data-none-option') && input.checked) {
          symptomInputs.forEach((item) => {
            if (item !== input) item.checked = false;
          });
        } else if (input.checked && noneOption) {
          noneOption.checked = false;
        }
      }

      if (group && group.querySelector('input:checked')) this.clearGroupError(group);
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    onDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialog.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onDialogClose() {
      if (this.lastDialogOpener?.isConnected) this.lastDialogOpener.focus();
      this.lastDialogOpener = null;
    }

    start() {
      this.startButton.hidden = true;
      if (this.intro) this.intro.hidden = true;
      this.experience.hidden = false;
      this.showStep(0);
    }

    showStep(index) {
      if (index < 0 || index >= this.steps.length) return;

      this.currentStep = index;
      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== index;
      });

      const visibleStep = this.steps[index];
      const heading = visibleStep.querySelector(index === 4 ? '[data-results-heading]' : '[data-step-heading]');
      const stepNames = ['Características de tu piel', 'Rutina', 'Hábitos', 'Sensibilidad e irritación', 'Resultados'];
      const numericStep = index + 1;

      this.progressbar?.setAttribute('aria-valuenow', String(numericStep));
      this.progressbar?.setAttribute('aria-valuetext', `Paso ${numericStep} de 5: ${stepNames[index]}`);
      if (this.progressFill) this.progressFill.style.setProperty('--diagnostic-progress', String(numericStep / 5));
      if (this.progressLabel) this.progressLabel.textContent = `Paso ${numericStep} de 5`;

      requestAnimationFrame(() => {
        visibleStep.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
        heading?.focus({ preventScroll: true });
      });
    }

    validateStep(index) {
      const step = this.steps[index];
      if (!step) return false;

      let firstInvalid = null;
      step.querySelectorAll('[data-required-group]').forEach((group) => {
        const isValid = Boolean(group.querySelector('input:checked'));
        if (isValid) {
          this.clearGroupError(group);
        } else {
          group.setAttribute('aria-invalid', 'true');
          const error = group.querySelector('[role="alert"]');
          if (error) error.hidden = false;
          if (!firstInvalid) firstInvalid = group.querySelector('input');
        }
      });

      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.closest('.diagnostic-question')?.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center'
        });
        return false;
      }

      return true;
    }

    clearGroupError(group) {
      group.removeAttribute('aria-invalid');
      const error = group.querySelector('[role="alert"]');
      if (error) error.hidden = true;
    }

    captureAnswers() {
      const answers = {};
      this.querySelectorAll('input:checked').forEach((input) => {
        if (input.type === 'checkbox') {
          if (!Array.isArray(answers[input.name])) answers[input.name] = [];
          answers[input.name].push(input.value);
        } else {
          answers[input.name] = input.value;
        }
      });
      this.answers = answers;
    }

    renderResults() {
      const wake = this.answers.wake;
      const shine = this.answers.shine;
      let tendency = 'balanced';

      if (wake === 'very-oily' || shine === 'all-face') {
        tendency = 'oily';
      } else if (wake === 'very-dry' && shine === 'never') {
        tendency = 'dry';
      } else if (wake === 'mixed' || shine === 't-zone') {
        tendency = 'mixed';
      }

      this.setText('[data-result-tendency]', tendencyCopy[tendency]);
      this.setText('[data-result-concern]', priorityCopy[this.answers.priority] || 'Una prioridad por conversar durante una valoración');

      const selectedSymptoms = Array.isArray(this.answers.symptoms) ? this.answers.symptoms : [];
      const needs = selectedSymptoms.includes('none')
        ? ['No señalaste ninguna de las necesidades incluidas en esta orientación.']
        : selectedSymptoms.map((value) => symptomCopy[value]).filter(Boolean);
      this.setList('[data-result-needs]', needs);

      const discussion = ['Conversar con Andrea sobre tu principal prioridad durante una valoración profesional.'];
      if (this.answers.sunscreen !== 'daily') discussion.push('Revisar el uso cotidiano de protección solar y resolver dudas sobre su aplicación.');
      if (this.answers.wash === 'never' || this.answers.wash === 'more-than-two') discussion.push('Comentar la frecuencia de limpieza y cómo se siente tu piel después.');
      if (this.answers.products === 'unadvised') discussion.push('Revisar si los productos actuales son compatibles entre sí y con lo que reportas.');
      if (this.answers.irritation !== 'never' || selectedSymptoms.some((value) => ['sensitivity', 'redness'].includes(value))) {
        discussion.push('Describir qué productos o situaciones coinciden con la sensibilidad o irritación que notas.');
      }
      this.setList('[data-result-discussion]', discussion);

      const education = ['Mantener una rutina sencilla y observar cómo responde tu piel antes de introducir varios cambios a la vez.'];
      if (this.answers.water === 'low') education.push('Considerar tus hábitos generales de hidratación como parte de la conversación de bienestar.');
      if (this.answers.sleep === 'under-five') education.push('Tener en cuenta el descanso que reportaste al conversar sobre tus prioridades de cuidado.');
      if (this.answers.stress === 'high') education.push('Compartir el contexto de estrés que reportaste, sin asumir que explica por sí solo los cambios de tu piel.');
      education.push('Evitar elegir un servicio solo a partir de este resumen; una valoración permite revisar el contexto directamente.');
      this.setList('[data-result-education]', education);

      const concerning = this.answers.irritation === 'frequent' || selectedSymptoms.some((value) => ['marks', 'redness', 'sensitivity', 'breakouts'].includes(value));
      const caution = concerning
        ? 'Como reportaste señales que podrían requerir atención adicional, busca evaluación médica o dermatológica si son persistentes, intensas, dolorosas, aparecen de forma repentina o incluyen heridas. Una orientación cosmética no sustituye esa evaluación.'
        : 'Si notas lesiones persistentes, irritación significativa, dolor, heridas o cambios repentinos, busca una evaluación médica o dermatológica antes de elegir cuidados cosméticos.';
      this.setText('[data-result-caution]', caution);
    }

    setText(selector, value) {
      const target = this.querySelector(selector);
      if (target) target.textContent = value;
    }

    setList(selector, values) {
      const list = this.querySelector(selector);
      if (!list) return;
      list.replaceChildren(...values.map((value) => {
        const item = document.createElement('li');
        item.textContent = value;
        return item;
      }));
    }

    resetOrientation() {
      this.querySelectorAll('input').forEach((input) => {
        input.checked = false;
      });
      this.querySelectorAll('[data-required-group]').forEach((group) => this.clearGroupError(group));
      this.querySelectorAll('[data-result-needs], [data-result-discussion], [data-result-education]').forEach((list) => list.replaceChildren());
      this.querySelectorAll('[data-result-tendency], [data-result-concern], [data-result-caution]').forEach((target) => {
        target.textContent = '';
      });

      this.answers = {};
      this.currentStep = 0;
      this.experience.hidden = true;
      this.steps.forEach((step, index) => {
        step.hidden = index !== 0;
      });
      this.startButton.hidden = false;
      if (this.intro) this.intro.hidden = false;
      this.startButton.focus();
      this.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }

    openHandoff(opener) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.lastDialogOpener = opener;
      this.dialog.showModal();
      this.dialog.querySelector('[data-handoff-close]')?.focus();
    }
  }

  customElements.define('skin-orientation', SkinOrientation);
})();
